import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CommonModalForm from '../../components/commonComponent/CommonModalForm';
import { validateForm } from '../../utils/formUtils';
import { toastSuccess, toastError } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';
import { FaEdit, FaTrash } from 'react-icons/fa';

const GrowthPartnerClusterPage = () => {
  const navigate = useNavigate();
  const [growthPartner, setGrowthPartner] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formValues, setFormValues] = useState({
    partner: '',
    tagSelect: '',
    clusters: [],
    chapters: [],
  });
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [dropdowns, setDropdowns] = useState({ tags: [], clusters: [], chapters: [] });

  const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT || 10);
  // Fetch dropdowns for tags, clusters, chapters
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const [tagRes, clusterRes, chapterRes] = await Promise.all([
          axios.get('/admin/api/tags'),
          axios.get('/admin/api/clusters'),
          axios.get('/admin/api/chapters'),
        ]);
        
        setDropdowns({
          tags: (tagRes.data?.tags || tagRes.data || [])?.map(t => ({ value: t._id, label: t.name })) || [],
          clusters: clusterRes.data?.clusters?.map(c => ({ value: c._id, label: c.name })) || [],
          chapters: chapterRes.data?.chapters?.map(ch => ({ value: ch._id, label: ch.name, cluster: ch.cluster })) || [],
        });
      } catch (err) {
        console.error('Failed to fetch dropdowns:', err);
      }
    };
    fetchDropdowns();
  }, []);

  // Filter chapters based on selected clusters
  const filteredChapters = formValues.clusters?.length > 0
    ? dropdowns.chapters.filter(ch => formValues.clusters.includes(ch.cluster))
    : dropdowns.chapters;

  useEffect(() => {
    fetchGrowthPartner(currentPage, searchQuery);
  }, [currentPage, searchQuery]);

  const fetchGrowthPartner = async (page = 1, search = '') => {
    try {
      const res = await axios.get(`/admin/api/growthPartnerCluster/all?page=${page}&limit=${limit}&search=${search}`);
      const { growthPartner = [], totalCount = 0 } = res.data;

      setGrowthPartner(growthPartner);
      setTotalPages(Math.ceil(totalCount / limit));
    } catch (err) {
      console.error('Failed to fetch Growth Partner:', err);
    }
  };

  const handleSave = async () => {
    const validationErrors = validateForm(formValues, {
      partner: { required: true },
      tagSelect: { required: true },
      clusters: { required: true },
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      if (editingId) {
        await axios.put(`/admin/api/growthPartnerCluster/${editingId}`, formValues);
        toastSuccess('Growth Partner updated successfully!');
      } else {
        await axios.post('/admin/api/growthPartnerCluster', formValues);
        toastSuccess('Growth Partner added successfully!');
      }
      setCurrentPage(1);
      fetchGrowthPartner(1, searchQuery);
      handleCancel();
    } catch (err) {
      console.error('Failed to save Growth Partner:', err);
      toastError('Failed to save Growth Partner');
    }
  };

  const handleEdit = (growthPartner) => {
    setFormValues({
      partner: growthPartner.partner || '',
      tagSelect: growthPartner.tagSelect || '',
      clusters: growthPartner.clusters || [],
    });
    setEditingId(growthPartner._id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this Growth Partner?')) {
      try {
        await axios.delete(`/admin/api/growthPartnerCluster/${id}`);
        toastSuccess('Growth Partner deleted successfully!');
        setCurrentPage(1);
        fetchGrowthPartner(1, searchQuery);
      } catch (err) {
        console.error('Failed to delete Growth Partner:', err);
        toastError('Failed to delete Growth Partner');
      }
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setFormValues({
      partner: '',
      tagSelect: '',
      clusters: [],
    });
    setEditingId(null);
    setErrors({});
  };

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0"> Growth Partner Cluster</h3>
        <input
          type="text"
          className="form-control w-25"
          placeholder="Search Growth Partner..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
        />
      </div>

      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center p-2">
          <h5 className="mb-0">Growth Partner List</h5>
          <button
            className="btn btn-sm btn-success"
            onClick={() => setShowModal(true)}
          >
            + Add Growth Partner
          </button>
        </div>
       <div className="table-responsive">
        <div className="card-body p-0">
          <table className="table table-bordered table-sm mb-0">
            <thead className="table-light">
              <tr>
                <th className="py-1 px-2 w-5">Sr no.</th>
                <th className="py-1 px-2">Partner</th>
                <th className="py-1 px-2">Tag</th>
                <th className="py-1 px-2">Clusters</th>
                <th className="py-1 px-2">Chapters</th>
                <th className="py-1 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {growthPartner.length > 0 ? (
                growthPartner.map((partner, idx) => {
                  const tagLabel = dropdowns.tags.find(t => t.value === partner.tagSelect)?.label || '';
                  const clusterLabels = partner.clusters?.map(clusterId => 
                    dropdowns.clusters.find(c => c.value === clusterId)?.label
                  ).filter(Boolean).join(', ') || '';
                  const chapterLabels = partner.chapters?.map(chapterId => 
                    dropdowns.chapters.find(ch => ch.value === chapterId)?.label
                  ).filter(Boolean).join(', ') || '';
                  
                  return (
                    <tr key={partner._id}>
                      <td className="py-1 px-2">{(currentPage - 1) * limit + idx + 1}</td>
                      <td className="py-1 px-2">
                        {partner.partner && (
                          <img 
                            src={partner.partner} 
                            alt="Partner" 
                            style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '50%' }}
                          />
                        )}
                      </td>
                      <td className="py-1 px-2">{tagLabel}</td>
                      <td className="py-1 px-2">{clusterLabels}</td>
                      <td className="py-1 px-2">{chapterLabels}</td>
                      <td className="py-1 px-2">
                        <button
                          className="btn btn-sm py-0 px-2 fs-6 lh-sm shadow-none border-0 text-primary-custom"
                          title="Edit"
                          onClick={() => handleEdit(partner)}
                        >
                          <FaEdit size={14} />
                        </button>
                        <button
                          className="btn btn-sm py-0 px-2 fs-6 lh-sm shadow-none border-0 text-danger"
                          onClick={() => handleDelete(partner._id)}
                          title="Delete"
                        >
                          <FaTrash size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-2">No Growth Partner found.</td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-end align-items-center gap-2 p-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                &laquo;
              </button>

             {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
               <button
                  key={page}
                  className="{`btn btn-sm page-btn ${currentPage === page ? 'active' : ''}`}"
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}

              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                &raquo;
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
      <CommonModalForm
        visible={showModal}
        title={editingId ? 'Edit Growth Partner' : 'Create Cluster Growth Partner'}
        fields={[
          { 
            name: 'partner', 
            label: 'Partner', 
            type: 'file', 
            accept: 'image/*',
            placeholder: 'Upload Partner Image' 
          },
          { 
            name: 'tagSelect', 
            label: 'Tag Select', 
            type: 'select', 
            options: dropdowns.tags, 
            placeholder: 'Select...' 
          },
          { 
            name: 'clusters', 
            label: 'Cluster', 
            type: 'select', 
            isMulti: true, 
            options: dropdowns.clusters, 
            placeholder: 'Select Cluster' 
          },
          ...(formValues.clusters?.length > 0 ? [
            { 
              name: 'chapters', 
              label: 'Chapters', 
              type: 'select', 
              isMulti: true, 
              options: filteredChapters, 
              placeholder: 'Select Chapter' 
            }
          ] : []),
        ]}
        values={formValues}
        errors={errors}
        onChange={setFormValues}
        onSubmit={handleSave}
        onCancel={handleCancel}
        submitLabel={editingId ? 'Update' : 'Submit'}
        requiredFields={['partner', 'tagSelect', 'clusters']}
      />
    </div>
  );
};

export default GrowthPartnerClusterPage;
